// src/screens/Planos/Planos.js

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { CheckCircle, ShieldCheck, Star, TrendingDown } from "lucide-react"; // Adicionei TrendingDown
import "./Planos.css";

// Mapa de temas para cada aplicativo
const appThemes = {
  calcleite: { color: "#0284c7", name: "CalcLeite" },
  calccafe: { color: "#854d0e", name: "CalcCafé" },
  calcbov: { color: "#16a34a", name: "CalcBov" },
  calcagro: { color: "#ca8a04", name: "CalcAgro" },
};

const Planos = () => {
  const { appKey } = useParams();
  const [planos, setPlanos] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [error, setError] = useState("");
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const theme = useMemo(
    () => appThemes[appKey] || { color: "#1b4332", name: "App" },
    [appKey],
  );

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }

    const fetchPlanos = async () => {
      setLoadingPlans(true);
      setError("");
      try {
        const response = await fetch(
          "https://backendcustosagro-e51a.onrender.com/prices",
        );
        if (!response.ok)
          throw new Error("Não foi possível carregar os planos.");

        const { data } = await response.json();
        const planosFiltrados = data.filter(
          (p) => p.app && p.app.toLowerCase().includes(appKey.toLowerCase()),
        );

        if (planosFiltrados.length === 0)
          throw new Error(`Nenhum plano para ${theme.name} encontrado.`);

        setPlanos(planosFiltrados);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingPlans(false);
      }
    };

    if (appKey) {
      fetchPlanos();
    } else {
      setError("Nenhum aplicativo selecionado.");
      setLoadingPlans(false);
    }
  }, [user, authLoading, navigate, appKey, theme.name]);

  const handleSelectPlan = (plan) => {
    navigate(`/${appKey}/pagamento`, { state: { plan } });
  };

  if (authLoading || loadingPlans) {
    return (
      <div className="planos-loading-container">
        <div className="spinner"></div>
        <p>Carregando melhores ofertas...</p>
      </div>
    );
  }

  if (error) {
    return <div className="planos-status erro">{error}</div>;
  }

  const themeColorStyle = {
    "--theme-color": theme.color,
  };

  return (
    <div className="planos-container" style={themeColorStyle}>
      <div className="planos-header">
        <span className="planos-header-tag">Acesso Completo</span>
        <h1>Planos para o {theme.name}</h1>
        <p>
          Escolha a opção ideal para o seu negócio e comece a economizar hoje.
        </p>
      </div>

      <div className="planos-grid">
        {planos.map((plan) => {
          const isYearly = plan.interval === "year";
          const hasTrial = plan.trial_days > 0;

          let priceDetail = null;
          let savingsTag = null;

          if (isYearly) {
            // Lógica para preço mensal equivalente
            if (plan.unit_amount) {
              const monthlyEquivalent = (
                plan.unit_amount /
                100 /
                12
              ).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
              priceDetail = `Equivalente a ${monthlyEquivalent}/mês`;
            }
            // Lógica visual de economia (Geralmente anual tem ~16% a 20% off)
            savingsTag = "ECONOMIZE 16%";
          }

          return (
            <div
              key={plan.stripe_id}
              className={`plano-card ${isYearly ? "destaque" : ""}`}
            >
              {/* Badge Principal (Topo do Card) */}
              {(hasTrial || isYearly) && (
                <div className="plano-badge">
                  {hasTrial
                    ? `${plan.trial_days} DIAS GRÁTIS`
                    : "MELHOR ESCOLHA"}
                </div>
              )}

              <div className="plano-content-wrapper">
                <h3>{plan.formatted_name}</h3>

                <div className="price-wrapper">
                  <p className="plano-price">{plan.formatted_price}</p>
                  <span className="plano-interval">
                    /{plan.formatted_interval}
                  </span>
                </div>

                {/* Seção de Economia e Detalhe */}
                <div className="price-extras">
                  {/* Tag de Porcentagem de Economia */}
                  {savingsTag && (
                    <span className="savings-pill">
                      <TrendingDown size={14} /> {savingsTag}
                    </span>
                  )}

                  {/* Preço equivalente mensal */}
                  {priceDetail && (
                    <p className="plano-price-detail">{priceDetail}</p>
                  )}
                </div>

                <p className="plano-description">
                  {plan.description || "Acesso total a todas as ferramentas."}
                </p>

                <ul className="plano-features">
                  <li>
                    <CheckCircle className="plano-check-icon" size={20} />
                    <span>
                      {hasTrial
                        ? `Teste grátis por ${plan.trial_days} dias`
                        : "Acesso imediato"}
                    </span>
                  </li>
                  <li>
                    <CheckCircle className="plano-check-icon" size={20} />
                    <span>Cálculos ilimitados</span>
                  </li>
                  <li>
                    <CheckCircle className="plano-check-icon" size={20} />
                    <span>Relatórios em PDF</span>
                  </li>
                  <li>
                    <ShieldCheck className="plano-check-icon" size={20} />
                    <span>Suporte Prioritário</span>
                  </li>
                </ul>
              </div>

              <button
                onClick={() => handleSelectPlan(plan)}
                className="plano-button"
              >
                {hasTrial ? "Começar Teste Grátis" : "Assinar Agora"}
              </button>
            </div>
          );
        })}
      </div>

      <div className="planos-footer-info">
        <p>
          <Star size={16} style={{ display: "inline", marginBottom: -2 }} />{" "}
          Satisfação garantida ou seu dinheiro de volta nos primeiros 7 dias.
        </p>
      </div>
    </div>
  );
};

export default Planos;
